/**
 * usePopularSubcategories Hook
 *
 * React Query hook for fetching popular subcategories (categories with parentId)
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchPopularSubcategories } from '../../api/fetchPopularSubcategories';
import type { Category } from '../../types';

export interface UsePopularSubcategoriesParams {
  limit?: number;
  useMock?: boolean;
  enabled?: boolean;
}

export interface UsePopularSubcategoriesReturn {
  subcategories: Category[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isRefetching: boolean;
}

/**
 * Query key factory for popular subcategories
 */
export const popularSubcategoriesKeys = {
  all: ['categories', 'popular', 'subcategories'] as const,
  byLimit: (limit: number, useMock: boolean) =>
    [...popularSubcategoriesKeys.all, limit, useMock] as const,
};

/**
 * Hook for fetching popular subcategories with React Query
 *
 * Features:
 * - Automatic caching (5 minutes stale time)
 * - Auto-refetch every 5 minutes
 * - Optimistic updates support
 * - Background refetching
 * - Auto-detects development mode from NEXT_PUBLIC_IS_DEVELOPMENT env var
 *
 * @param params - Configuration for fetching subcategories
 * @returns Object with subcategories, loading state, error, and refetch function
 */
export function usePopularSubcategories(
  params: UsePopularSubcategoriesParams = {},
): UsePopularSubcategoriesReturn {
  // Auto-detect development mode from environment variable
  const isDevelopment = process.env.NEXT_PUBLIC_IS_DEVELOPMENT === 'true';
  const { limit = 5, useMock = isDevelopment, enabled = true } = params;

  const {
    data: subcategories = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: popularSubcategoriesKeys.byLimit(limit, useMock),
    queryFn: () => fetchPopularSubcategories({ limit, useMock }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    subcategories,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refetch: () => {
      refetch();
    },
    isRefetching,
  };
}
